import PageNavbar from '../../components/feature/PageNavbar';

export default function CentrePage() {
  const pillars = [
    {
      number: '01',
      title: 'Robotics',
      description: 'Advanced robotic systems providing precise, repeatable movements and real-time feedback for enhanced rehabilitation outcomes.',
      color: 'bg-teal-500'
    },
    {
      number: '02',
      title: 'Virtual Reality (VR)',
      description: 'Immersive environments creating engaging and effective therapy sessions through interactive experiences and real-world simulation.',
      color: 'bg-rose-500'
    },
    {
      number: '03',
      title: 'Gamification',
      description: 'Motivating and engaging therapy through game-based exercises that make rehabilitation more enjoyable and effective.',
      color: 'bg-cyan-500'
    },
    {
      number: '04',
      title: 'Artificial Intelligence (AI)',
      description: 'Smart systems that adapt and personalise treatment programs based on real-time patient progress and individual needs.',
      color: 'bg-indigo-500'
    },
    {
      number: '05',
      title: 'FES (Functional Electrical Stimulation)',
      description: 'Targeted electrical stimulation to activate muscles and nerves, promoting functional movement and accelerating recovery outcomes.',
      color: 'bg-emerald-500'
    },
    {
      number: '06',
      title: 'Exoskeleton',
      description: 'Wearable robotic suits that provide powered assistance for walking, standing, and movement training to restore mobility and independence.',
      color: 'bg-amber-500'
    },
    {
      number: '07',
      title: 'Tele Rehab for Remote Areas',
      description: 'Remote rehabilitation services bringing expert care to patients in rural and remote locations through advanced telehealth technology.',
      color: 'bg-sky-500'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PageNavbar />

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Innovation Serving Humanity</h2>
          <p className="text-gray-600">Through advanced rehabilitation technology and interdisciplinary care</p>
        </div>

        {/* Mission Section */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <span className="inline-block px-4 py-2 bg-teal-50 text-teal-600 rounded-full text-sm font-medium mb-6">
              Our Mission
            </span>
            <p className="text-gray-700 leading-relaxed mb-6">
              At ARC, we believe that <strong className="text-teal-600">innovation should serve humanity</strong>. Our interdisciplinary care team includes exercise physiologists, neurologists, physiotherapists, occupational therapists, psychologists, and rehabilitation physicians who collaborate to provide a comprehensive and coordinated treatment pathway.
            </p>
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border-l-4 border-teal-500 p-6 rounded-r-lg">
              <p className="text-gray-700">
                <strong className="text-gray-900">Personalized Care:</strong> Each program is tailored to meet the goals of the patient using our five core pillars of innovation.
              </p>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://static.readdy.ai/image/c1d3a3065b1a0920ec31881e87c350dd/691714ac6349becaeaa861c903431034.jpeg"
                alt="Expert medical team"
                className="w-full h-80 object-cover object-top"
              />
            </div>
          </div>
        </div>

        {/* Five Pillars Section */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-2 bg-teal-50 text-teal-600 rounded-full text-sm font-medium mb-4">
            Seven Pillars
          </span>
          <h3 className="text-3xl font-bold text-gray-900 mb-4">Our Innovation Framework</h3>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Cutting-edge technologies working together to accelerate your recovery journey — powered by the brain&apos;s natural ability to rewire and heal through neuroplasticity.
          </p>
        </div>

        {/* Pillars Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {pillars.slice(0, 6).map((pillar, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 group"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className={`${pillar.color} text-white text-sm font-bold px-3 py-1 rounded-full`}>
                  {pillar.number}
                </span>
                <h4 className="text-lg font-semibold text-gray-900">{pillar.title}</h4>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">{pillar.description}</p>
            </div>
          ))}
        </div>

        {/* Last Pillar - Full Width */}
        <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 group max-w-md">
          <div className="flex items-center gap-3 mb-4">
            <span className={`${pillars[6].color} text-white text-sm font-bold px-3 py-1 rounded-full`}>
              {pillars[6].number}
            </span>
            <h4 className="text-lg font-semibold text-gray-900">{pillars[6].title}</h4>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed">{pillars[6].description}</p>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-100 mt-16">
        <div className="max-w-7xl mx-auto px-6 py-8 text-center">
          <p className="text-gray-500 text-sm">© 2025 Accelerated Recovery Centre (ARC). All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
